import React, { useEffect, useRef, useState } from 'react'
import { InputSwitch } from 'primereact/inputswitch'
import { Panel } from 'primereact/panel'
import { Tag } from 'primereact/tag'
import { Tooltip } from 'primereact/tooltip'
import { Link } from 'react-router-dom'

import {
    ChargePoint,
    useApiCustomerGetAllQuery,
    useChargePointUpdateChargerInformationMutation,
} from '../../redux/api/web-api'
import { mapChargerToChargerInformation } from '../../utils/objectMappers'
import useText from '../../hooks/useText'
import { useToast } from '../../uikit/ToastProvider'
import { Dropdown } from '../../uikit/Dropdown'
import { FieldInfoButton } from '../../uikit/field-info-button'
import { isSupportDomain } from '../../../lib/currentDomain'

export type EapiCallbacksPanelProps = {
    chargePoint: ChargePoint
}

export function EapiCallbacksPanel({ chargePoint }: EapiCallbacksPanelProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const callbackToggleWrapper = useRef<HTMLSpanElement>(null)
    const assignedApiCustomerId = chargePoint.additionalInfo?.callbackEnabledApiCustomerId ?? null
    const [callbackEnabled, setCallbackEnabled] = useState(assignedApiCustomerId !== null)
    const [selectedApiCustomerId, setSelectedApiCustomerId] = useState(assignedApiCustomerId)
    const { data: apiCustomers, isLoading: apiCustomersIsLoading } = useApiCustomerGetAllQuery(
        {
            enabled: true,
            callbackEnabled: true,
        },
        { refetchOnFocus: true }
    )
    const [updateChargePoint, { isLoading: callbacksUpdating }] = useChargePointUpdateChargerInformationMutation()

    useEffect(() => {
        setCallbackEnabled(assignedApiCustomerId !== null)
        setSelectedApiCustomerId(assignedApiCustomerId)
    }, [assignedApiCustomerId, chargePoint.id])

    const apiCustomerOptions =
        apiCustomers?.map((customer) => ({
            value: customer.id,
            label: `[${customer.id}] ${customer.name}`,
        })) ?? []
    const callbackCustomerRequired = !callbackEnabled && selectedApiCustomerId === null

    const updateEapi = async () => {
        const enableCallbacks = !callbackEnabled
        if (enableCallbacks && selectedApiCustomerId === null) return

        try {
            await updateChargePoint({
                chargerInformation: {
                    ...mapChargerToChargerInformation(chargePoint),
                    callbackEnabledApiCustomerId: enableCallbacks ? selectedApiCustomerId : null,
                },
                chargePointId: chargePoint.id,
            }).unwrap()
            setCallbackEnabled(enableCallbacks)
            toast.notifySuccess(text('msgChargerUpdated'))
        } catch (error) {
            toast.notifyError(text('msgChargePointNotUpdated'))
        }
    }

    return (
        <Panel
            header={
                <>
                    {text('lblEapiCustomersWidget')}
                    <Tag
                        className="ml-2"
                        severity={callbackEnabled ? 'success' : null}
                        value={callbackEnabled ? text('lblEnabledStatus') : text('lblDisabledStatus')}
                    />
                </>
            }
            className="flex-1 min-w-0"
            data-testid="eapiCustomer-heading"
            icons={
                <div className="flex align-items-center gap-3">
                    {isSupportDomain && (
                        <Link
                            className="p-button p-component p-button-outlined p-button-sm no-underline"
                            to="/eapi"
                            data-testid="eapi-redirect"
                        >
                            <span className="p-button-label">{text('lblGoToEapiCallbacks')}</span>
                        </Link>
                    )}

                    <FieldInfoButton
                        ariaLabel={text('lblMoreInformation')}
                        text={text('lblEapiExplanationText')}
                        testId="eapi-info-text"
                    />
                </div>
            }
        >
            <div className="flex w-full align-items-center gap-2">
                <Dropdown
                    className="flex-1 min-w-0"
                    id="callbackEnabledApiCustomerId"
                    options={apiCustomerOptions}
                    value={selectedApiCustomerId}
                    placeholder={text('lblSelectCallbackCustomer')}
                    disabled={callbackEnabled || callbacksUpdating}
                    filter
                    loading={apiCustomersIsLoading || callbacksUpdating}
                    onChange={(option) => setSelectedApiCustomerId(option.value ?? null)}
                />

                {callbackCustomerRequired && (
                    <Tooltip
                        target={callbackToggleWrapper}
                        content={text('msgEapiCallbackToggleRequiresSelection')}
                        position="top"
                    />
                )}
                <span
                    ref={callbackToggleWrapper}
                    className="inline-flex flex-shrink-0"
                    data-testid="eapi-toggle-wrapper"
                    aria-busy={callbacksUpdating}
                >
                    <InputSwitch
                        checked={callbackEnabled}
                        disabled={callbackCustomerRequired || callbacksUpdating}
                        onChange={() => void updateEapi()}
                        data-testid="eapi-toggle"
                    />
                </span>
            </div>
        </Panel>
    )
}
